import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAdminOffers } from "@/hooks/api/use-admin";
import { adminService } from "@/api/services/admin.service";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Search, Eye, CheckCircle, XCircle, Trash2, Calendar, DollarSign, Building2, RefreshCw, MoreVertical, FileText, Clock, CheckCircle2 } from "lucide-react";

const AdminOffers = () => {
  const { t, isRTL } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "accepted" | "rejected">("all");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filters = {
    limit: 15,
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
  };

  const { data, isLoading, error, refetch } = useAdminOffers(filters);

  // Handle the actual API response structure
  const offers = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.meta || {};

  // Helper functions
  const getTranslatedText = (field: string | { en: string; ar: string }) => {
    if (typeof field === 'string') return field;
    return field[isRTL ? 'ar' : 'en'] || field.en || '';
  };

  // Mutations for actions
  const acceptOfferMutation = useMutation({
    mutationFn: adminService.acceptOffer,
    onSuccess: (data: any) => {
      toast({
        title: t('offers.accepted') || 'Offer Accepted',
        description: t('offers.acceptSuccess') || 'The offer has been accepted successfully.',
      });
      
      // Check for email error and show additional toast
      if (data.meta?.email_error || data.email_error) {
        const emailError = data.meta?.email_error || data.email_error;
        setTimeout(() => {
          toast({
            title: 'Email Notification Issue',
            description: emailError.message,
            variant: 'destructive',
          });
        }, 1000);
      }
      
      queryClient.invalidateQueries({ queryKey: ['admin', 'offers'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error') || 'Error',
        description: error?.message || t('offers.acceptError') || 'Failed to accept offer.',
        variant: 'destructive',
      });
    },
  });

  const rejectOfferMutation = useMutation({
    mutationFn: adminService.rejectOffer,
    onSuccess: (data: any) => {
      toast({
        title: t('offers.rejected') || 'Offer Rejected',
        description: t('offers.rejectSuccess') || 'The offer has been rejected successfully.',
      });
      
      // Check for email error and show additional toast
      if (data.meta?.email_error || data.email_error) {
        const emailError = data.meta?.email_error || data.email_error;
        setTimeout(() => {
          toast({
            title: 'Email Notification Issue',
            description: emailError.message,
            variant: 'destructive',
          });
        }, 1000);
      }
      
      queryClient.invalidateQueries({ queryKey: ['admin', 'offers'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error') || 'Error',
        description: error?.message || t('offers.rejectError') || 'Failed to reject offer.',
        variant: 'destructive',
      });
    },
  });

  const deleteOfferMutation = useMutation({
    mutationFn: adminService.deleteOffer,
    onSuccess: () => {
      toast({
        title: t('offers.deleted') || 'Offer Deleted',
        description: t('offers.deleteSuccess') || 'The offer has been deleted successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'offers'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common.error') || 'Error',
        description: error?.message || t('offers.deleteError') || 'Failed to delete offer.',
        variant: 'destructive',
      });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200 font-medium">{isRTL ? 'معلق' : 'Pending'}</Badge>;
      case "accepted":
        return <Badge className="bg-green-100 text-green-800 border-green-200 font-medium">{isRTL ? 'مقبول' : 'Accepted'}</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800 border-red-200 font-medium">{isRTL ? 'مرفوض' : 'Rejected'}</Badge>;
      default:
        return null;
    }
  };


  const filteredOffers = statusFilter === "all" 
    ? offers 
    : offers.filter((offer: any) => offer.status === statusFilter);

  // Calculate stats from offers
  const stats = {
    total: offers.length,
    pending: offers.filter((o: any) => o.status === 'pending').length,
    accepted: offers.filter((o: any) => o.status === 'accepted').length,
    rejected: offers.filter((o: any) => o.status === 'rejected').length,
  };

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl">{isRTL ? 'العروض' : 'Offers'}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-2 sm:gap-4">
                  <Skeleton className="h-10 w-10 sm:h-12 sm:w-12 rounded" />
                  <div className="space-y-1 sm:space-y-2 flex-1">
                    <Skeleton className="h-3 sm:h-4 w-3/4" />
                    <Skeleton className="h-2 sm:h-3 w-1/2" />
                  </div>
                  <Skeleton className="h-6 sm:h-8 w-16 sm:w-20 rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl">{isRTL ? 'العروض' : 'Offers'}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="text-center py-8 sm:py-12">
              <div className="text-red-500 dark:text-red-400 mb-4">
                <XCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 text-slate-900 dark:text-slate-100">
                {isRTL ? 'خطأ في التحميل' : 'Error Loading'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {isRTL ? 'حدث خطأ أثناء تحميل العروض' : 'Failed to load offers'}
              </p>
              <Button onClick={() => refetch()} variant="outline" className="text-xs sm:text-sm">
                {isRTL ? 'إعادة المحاولة' : 'Retry'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/50 dark:to-blue-900/30 border-blue-200 dark:border-blue-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 font-medium">
                  {isRTL ? 'إجمالي العروض' : 'Total Offers'}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/50 dark:to-amber-900/30 border-amber-200 dark:border-amber-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-amber-600 dark:text-amber-400 font-medium">
                  {isRTL ? 'معلقة' : 'Pending'}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/50 dark:to-green-900/30 border-green-200 dark:border-green-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 font-medium">
                  {isRTL ? 'مقبولة' : 'Accepted'}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-green-700 dark:text-green-300">{stats.accepted}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/50 dark:to-red-900/30 border-red-200 dark:border-red-800">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-red-500/10">
                <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-red-600 dark:text-red-400 font-medium">
                  {isRTL ? 'مرفوضة' : 'Rejected'}
                </p>
                <p className="text-lg sm:text-2xl font-bold text-red-700 dark:text-red-300">{stats.rejected}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg sm:text-xl">{isRTL ? 'العروض' : 'Offers'}</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {isRTL ? 'إدارة جميع عروض العملاء' : 'Manage all client offers'}
              </CardDescription>
            </div>
            <Button 
              onClick={() => refetch()} 
              variant="outline" 
              className="w-full sm:w-auto"
            >
              <RefreshCw size={14} className={`sm:w-4 sm:h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              <span className="text-xs sm:text-sm">{isRTL ? 'تحديث' : 'Refresh'}</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 mb-4 sm:mb-6">
            <div className="relative flex-1">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
              <Input
                placeholder={isRTL ? 'ابحث عن العروض...' : 'Search offers...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} text-xs sm:text-sm h-9 sm:h-10`}
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: "all" | "pending" | "accepted" | "rejected") => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-[180px] text-xs sm:text-sm h-9 sm:h-10">
                <SelectValue placeholder={isRTL ? 'تصفية حسب الحالة' : 'Filter by status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</SelectItem>
                <SelectItem value="pending">{isRTL ? 'معلق' : 'Pending'}</SelectItem>
                <SelectItem value="accepted">{isRTL ? 'مقبول' : 'Accepted'}</SelectItem>
                <SelectItem value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredOffers.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <div className="mb-4">
                <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-muted/50 flex items-center justify-center">
                  <Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-muted-foreground" />
                </div>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                {isRTL ? 'لا توجد عروض' : 'No Offers Found'}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {statusFilter === 'all' 
                  ? (isRTL ? 'لا توجد عروض في النظام حالياً' : 'No offers in the system yet') 
                  : (isRTL ? 'لا توجد عروض بهذه الحالة' : 'No offers with this status')
                }
              </p>
            </div>
          ) : (
            <>
              {/* Mobile & Tablet Cards View */}
              <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {filteredOffers.map((offer: any) => (
                  <Card key={offer.id} className="shadow-sm hover:shadow-md transition-shadow border-l-4" style={{
                    borderLeftColor: offer.status === 'accepted' ? '#22c55e' : offer.status === 'rejected' ? '#ef4444' : offer.status === 'pending' ? '#f59e0b' : '#e5e7eb'
                  }}>
                    <CardContent className="p-4">
                      {/* Header with title and status */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate mb-1">
                            {getTranslatedText(offer.tender?.title) || (isRTL ? 'بدون عنوان' : 'No Title')}
                          </h3>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Building2 size={12} className="flex-shrink-0" />
                            <span className="truncate">{offer.client?.company_name || (isRTL ? 'بدون اسم' : 'No Name')}</span>
                          </div>
                        </div>
                        {getStatusBadge(offer.status)}
                      </div>
                      
                      {/* Info Grid */}
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div className="flex items-center gap-1.5 p-2 rounded-md bg-muted/50">
                          <DollarSign size={14} className="text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-[10px] text-muted-foreground">{isRTL ? 'المبلغ' : 'Amount'}</p>
                            <p className="font-semibold text-foreground">
                              {offer.proposed_amount ? `${isRTL ? 'ر.س' : ''}${Number(offer.proposed_amount).toLocaleString()}${isRTL ? '' : ' SAR'}` : (isRTL ? 'غير محدد' : 'N/A')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 p-2 rounded-md bg-muted/50">
                          <Calendar size={14} className="text-blue-600 flex-shrink-0" />
                          <div>
                            <p className="text-[10px] text-muted-foreground">{isRTL ? 'التاريخ' : 'Date'}</p>
                            <p className="font-medium text-foreground">
                              {new Date(offer.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Notes */}
                      {offer.notes && (
                        <div className="mb-3 p-2 rounded-md bg-muted/30">
                          <p className="text-xs text-muted-foreground line-clamp-2">{offer.notes}</p>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/dashboard/offers/${offer.id}`)}
                          className="h-8 text-xs gap-1"
                        >
                          <Eye size={14} />
                          {isRTL ? 'عرض' : 'View'}
                        </Button>
                        <div className="flex gap-1">
                          {offer.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => acceptOfferMutation.mutate(offer.id)}
                                disabled={acceptOfferMutation.isPending}
                                className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => rejectOfferMutation.mutate(offer.id)}
                                disabled={rejectOfferMutation.isPending}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle size={16} />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteOfferMutation.mutate(offer.id)}
                            disabled={deleteOfferMutation.isPending}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs sm:text-sm font-semibold">{isRTL ? 'المناقصة' : 'Tender'}</TableHead>
                      <TableHead className="text-xs sm:text-sm font-semibold">{isRTL ? 'العميل' : 'Client'}</TableHead>
                      <TableHead className="text-xs sm:text-sm font-semibold">{isRTL ? 'المبلغ المقترح' : 'Proposed Amount'}</TableHead>
                      <TableHead className="text-xs sm:text-sm font-semibold">{isRTL ? 'تاريخ التقديم' : 'Submitted Date'}</TableHead>
                      <TableHead className="text-xs sm:text-sm font-semibold">{isRTL ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead className={`text-xs sm:text-sm font-semibold ${isRTL ? 'text-left' : 'text-right'}`}>{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOffers.map((offer: any) => (
                      <TableRow key={offer.id} className="hover:bg-muted/30 transition-colors">
                        <TableCell className="font-medium text-xs sm:text-sm">
                          <div className="min-w-0 max-w-[200px]">
                            <div className="truncate font-medium">{getTranslatedText(offer.tender?.title) || (isRTL ? 'بدون عنوان' : 'No Title')}</div>
                            {offer.notes && (
                              <div className="text-xs text-muted-foreground truncate mt-0.5">
                                {offer.notes}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Building2 size={14} className="text-primary" />
                            </div>
                            <span className="truncate max-w-[120px] font-medium">{offer.client?.company_name || (isRTL ? 'بدون اسم' : 'No Name')}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-green-700 dark:text-green-400">
                              {offer.proposed_amount ? `${isRTL ? 'ر.س ' : ''}${Number(offer.proposed_amount).toLocaleString()}${isRTL ? '' : ' SAR'}` : (isRTL ? 'غير محدد' : 'N/A')}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={14} />
                            <span>{new Date(offer.created_at).toLocaleDateString()}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(offer.status)}</TableCell>
                        <TableCell className={`${isRTL ? 'text-left' : 'text-right'}`}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical size={14} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isRTL ? "start" : "end"}>
                              <DropdownMenuItem onClick={() => navigate(`/admin/dashboard/offers/${offer.id}`)} className="text-xs sm:text-sm">
                                <Eye size={14} className="mr-2" />
                                {isRTL ? 'عرض' : 'View'}
                              </DropdownMenuItem>
                              {offer.status === 'pending' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => acceptOfferMutation.mutate(offer.id)}
                                    disabled={acceptOfferMutation.isPending}
                                    className="text-xs sm:text-sm"
                                  >
                                    <CheckCircle size={14} className="mr-2" />
                                    {acceptOfferMutation.isPending ? (isRTL ? 'جاري المعالجة...' : 'Processing...') : (isRTL ? 'قبول' : 'Accept')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => rejectOfferMutation.mutate(offer.id)}
                                    disabled={rejectOfferMutation.isPending}
                                    className="text-xs sm:text-sm text-red-600"
                                  >
                                    <XCircle size={14} className="mr-2" />
                                    {rejectOfferMutation.isPending ? (isRTL ? 'جاري المعالجة...' : 'Processing...') : (isRTL ? 'رفض' : 'Reject')}
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => deleteOfferMutation.mutate(offer.id)}
                                disabled={deleteOfferMutation.isPending}
                                className="text-xs sm:text-sm text-destructive"
                              >
                                <Trash2 size={14} className="mr-2" />
                                {deleteOfferMutation.isPending ? (isRTL ? 'جاري المعالجة...' : 'Processing...') : (isRTL ? 'حذف' : 'Delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOffers;
